import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  input,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSliderModule } from '@angular/material/slider';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Colord, colord } from 'colord';
import { Observable, ReplaySubject } from 'rxjs';
import { map, withLatestFrom, debounceTime } from 'rxjs/operators';
import { TypedFormControl } from '../../forms/typed-form-group';
import { NgCommonModule } from '../../ng-common.module';
import { ColourPickerComponent } from '../../colour-picker/colour-picker.component';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
  selector: 'pt-colour-selector',
  templateUrl: './colour-selector.component.html',
  styleUrls: ['./colour-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgCommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSliderModule,
    MatMenuModule,
    ColourPickerComponent,
    MatTooltip,
  ],
})
export class ColourSelectorComponent {
  private _snackbar = inject(MatSnackBar);
  palette$ = new ReplaySubject<string[]>(1);
  selectedColour$ = new ReplaySubject<Colord | undefined>(1);
  initialColour$ = new ReplaySubject<string>(1);
  hexColour$: Observable<string>;
  opacityCtrl = new TypedFormControl<number>();
  customColourCtrl = new TypedFormControl<string>('');

  displayPreviewColour = input(true);
  displayCompactPreviewColour = input(false);
  @Input() enableCustomInput = false;
  @Input() disableOpacity = false;
  @Input() opacityMin = 0;
  @Input() opacityMax = 1;

  @Input()
  set colour(colour: string) {
    if (colour) {
      this.selectColour(colour);
    }
  }

  @Input()
  set palette(palette: string[]) {
    if (palette) {
      this.palette$.next(palette);
    }
  }

  @Output() colourChange = new EventEmitter<string | undefined>();

  constructor() {
    this.opacityCtrl.valueChanges
      .pipe(withLatestFrom(this.selectedColour$), takeUntilDestroyed())
      .subscribe(([alpha, colour]) => {
        if (colour) {
          this.selectedColour$.next(colour.alpha(alpha));
        }
      });

    this.hexColour$ = this.selectedColour$.pipe(
      map((colour) => (colour ? colour.toHex() : ''))
    );

    this.selectedColour$
      .pipe(debounceTime(200), takeUntilDestroyed())
      .subscribe((colour) => {
        const value = colour ? colour.toHex() : '';
        this.customColourCtrl.setValue(value, { emitEvent: false });
        this.colourChange.emit(value);
      });

    this.customColourCtrl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((inputValue) => {
        if (!inputValue) {
          return;
        }

        const convertedColour = colord(inputValue);
        if (!convertedColour.isValid()) {
          this._snackbar.open('Invalid Hex Colour');
          return;
        }
        this.selectColour(inputValue);
      });
  }

  selectColour(colour?: string, $event?: MouseEvent): void {
    if ($event) {
      $event.stopPropagation();
    }
    if (!colour) {
      this.selectedColour$.next(undefined);
      return;
    }
    const convertedColour = colord(colour);
    if (convertedColour.isValid()) {
      this.selectedColour$.next(convertedColour);
      this.opacityCtrl.setValue(convertedColour.alpha());
    }
  }

  onCustomColourInput(): void {
    const inputValue = this.customColourCtrl.value;
    if (!inputValue) {
      return;
    }

    const convertedColour = colord(inputValue);
    if (!convertedColour.isValid()) {
      this._snackbar.open('Invalid Hex Colour');
      return;
    }
    this.selectColour(inputValue);
  }

  formatPercentage(value: number): string {
    return `${Math.round(value * 100)}%`;
  }

  isWhite(colour: string | null | undefined): boolean {
    if (!colour) {
      return false;
    }
    const normalised = colour.toLowerCase().replace(/\s/g, '');
    return (
      normalised === '#fff' ||
      normalised === '#ffffff' ||
      normalised === '#ffffffff' ||
      normalised === 'white' ||
      normalised === 'rgb(255,255,255)' ||
      normalised === 'rgba(255,255,255,1)'
    );
  }
}
